/**
 * Shared narration styling helpers for amortization / preview tables.
 *
 * Single source of truth for how each schedule-row TYPE is presented — narration
 * Chip colour, flow direction, ROW background band, and signed-amount formatting —
 * so the schedule table, the approval modal, and the rate-change comparison table
 * all render rows identically. Style rules live here, keyed by narration type; they
 * are NOT hand-coded per <TableRow> at each call site.
 */

import React from 'react';
import { formatNumber } from './formatters';

// Row-background palette, keyed by row category. Kept together so the whole
// colour scheme is legible in one place and a new category is one edit here.
//   override — amber; a manual per-row override is applied (kept as a row BAND)
//   group    — the collapsed multi-investor period header (clickable)
//   default  — normal striping (call site supplies odd/even)
// NOTE: Interest Accrual Adjustment rows are NOT given a row band — they render
// with standard striping like a payment row and are distinguished only by their
// two maroon chips (flow chip 'ADJ' + narration chip). A full-row band broke the
// table theme/striping.
export const NARRATION_ROW_STYLES = {
  override: { bg: '#fff8e1', hover: '#fff3cd' },
  group:    { bg: '#f0f4ff', hover: '#e3eafc' },
};

// Deep maroon used for BOTH the Interest Accrual Adjustment chips — the narration
// chip and the 'ADJ' flow chip — so the row's type reads at a glance. Maroon
// avoids purple (WHT), red (outflow) and blue (inflow).
export const ADJUSTMENT_CHIP_COLOR = '#7b1f2f';

// The manual interest carry-over row was renamed 'Carried Interest Accrual' ->
// 'Interest Accrual Adjustment' (DB migration T3-079). Display special-cases must
// match BOTH: approval snapshots freeze the narration TEXT in JSONB at submit time
// and are immutable, so a pre-rename snapshot still carries the old label and must
// keep rendering as an accrual-style adjustment row.
export const ADJUSTMENT_NARRATIONS = ['Interest Accrual Adjustment', 'Carried Interest Accrual'];
export const isAdjustmentNarration = (narration) => ADJUSTMENT_NARRATIONS.includes(narration);

// Determine if an entry is an inflow / outflow / accrual based on its narration.
export const getNarrationFlowType = (narration) => {
  if (!narration) return 'neutral';
  // Inflows — money coming IN from the investor
  if (narration.includes('Drawdown') || narration.includes('Opening Balance') || narration.includes('Injection')) return 'inflow';
  // Outflows — money going OUT to the investor
  if (narration.includes('Payment') || narration.includes('Fee') || narration.includes('Duty') || narration.includes('Withdrawal')) return 'outflow';
  // Accruals — not actual cash flow
  if (narration.includes('Accrual') || narration === 'Interest Float') return 'accrual';
  return 'neutral';
};

// MUI Chip `color` for a narration.
export const getNarrationColor = (narration) => {
  if (!narration) return 'default';
  if (narration.includes('Injection')) return 'success';
  if (narration.includes('Withdrawal')) return 'warning';
  const flowType = getNarrationFlowType(narration);
  if (flowType === 'inflow') return 'info';
  if (flowType === 'outflow') return 'error';
  if (flowType === 'accrual') return 'warning';
  return 'default';
};

// Full prop set for the narration Chip so call sites stay one-liners.
// Interest Accrual Adjustment (was 'Carried Interest Accrual') reads as a distinct
// (maroon) category. Match both labels for pre-rename approval snapshots.
export const getNarrationChipProps = (narration) => {
  if (isAdjustmentNarration(narration)) {
    return {
      variant: 'outlined',
      sx: { color: ADJUSTMENT_CHIP_COLOR, borderColor: ADJUSTMENT_CHIP_COLOR, fontWeight: 600 },
    };
  }
  return {
    variant: 'outlined',
    color: getNarrationColor(narration),
  };
};

// Flow chip ({ label, color?, sx? }) for a row, derived from flow TYPE — the shared
// rule the schedule table, approval modal and client portal all delegate to (was a
// 4× duplicated local `getFlowLabel`). Interest Accrual Adjustment shows a maroon
// 'ADJ' chip; it is checked BEFORE the generic accrual case (an adjustment is
// flow-type 'accrual'). This is DISPLAY ONLY — nothing computes off the label, and
// getNarrationFlowType still returns 'accrual' so balance/formatting/totals are
// unchanged. IN/OUT/ACC keep their existing semantic colours.
export const getNarrationFlowLabel = (narration) => {
  if (isAdjustmentNarration(narration)) {
    return { label: 'ADJ', sx: { color: ADJUSTMENT_CHIP_COLOR, borderColor: ADJUSTMENT_CHIP_COLOR, fontWeight: 600 } };
  }
  const flowType = getNarrationFlowType(narration);
  if (flowType === 'inflow') return { label: 'IN', color: 'info' };
  if (flowType === 'outflow') return { label: 'OUT', color: 'error' };
  if (flowType === 'accrual') return { label: 'ACC', color: 'warning' };
  return { label: '-', color: 'default' };
};

// Format a schedule amount by flow direction — the single formatter shared by the
// schedule table, approval modal and comparison table (was duplicated 3×). Uses
// React.createElement (not JSX) so this stays a plain .js util.
//   outflow  — red accounting parentheses:  (699.33)   [cash going out]
//   inflow   — blue plain:                   1,000.00
//   accrual/ — plain; but a NEGATIVE value (e.g. an Interest Accrual Adjustment
//   neutral    reduction) renders in DEFAULT-colour accounting parentheses (425.08),
//              matching the table convention. A raw minus is never shown.
export const formatNarrationAmount = (value, flowType) => {
  if (!value && value !== 0) return '-';
  const num = parseFloat(value);
  if (num === 0) return '-';
  if (flowType === 'outflow') {
    return React.createElement('span', { style: { color: '#d32f2f' } }, `(${formatNumber(num, 2)})`);
  }
  if (flowType === 'inflow') {
    return React.createElement('span', { style: { color: '#1976d2' } }, formatNumber(num, 2));
  }
  // accrual / neutral: negative -> default-colour parentheses; positive -> plain.
  if (num < 0) {
    return React.createElement('span', null, `(${formatNumber(Math.abs(num), 2)})`);
  }
  return formatNumber(num, 2);
};

// The MUI `sx` rule for a schedule row, resolved from its TYPE — the single place
// that owns row styling (folds in the old per-<TableRow> `hasOverride ? …`
// conditionals).
//
//  - Manual override -> amber background BAND (a genuine "this row was edited" flag,
//                       kept as a band per existing convention + legend).
//  - Group header    -> blue background + hover (it is clickable/collapsible).
//  - default         -> normal odd/even striping.
//
// Interest Accrual Adjustment rows intentionally get NO band here — they render with
// standard striping like a payment row and are marked only by their two maroon chips.
//
// Hover behaviour is tied to INTERACTIVITY: only the clickable group header gets a
// hover-shift; the override band pins its hover to its own colour so a non-clickable
// row doesn't look selectable.
export const getNarrationRowSx = (narration, { hasOverride = false, striped = true, isGroup = false } = {}) => {
  if (hasOverride) {
    const s = NARRATION_ROW_STYLES.override;
    return { backgroundColor: s.bg, '&:hover': { backgroundColor: s.bg } };
  }
  if (isGroup) {
    const s = NARRATION_ROW_STYLES.group;
    return { backgroundColor: s.bg, '&:hover': { backgroundColor: s.hover } };
  }
  if (striped) {
    return { '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } };
  }
  return {};
};
